import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Heart, Check, Clock, XCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";


export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const res = await fetch(`${API}/checkout/status/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          setPaymentData(data);

          if (data.payment_status === 'paid') {
            setStatus('success');
            return true;
          } else if (data.status === 'expired') {
            setStatus('expired');
            return true;
          }
        }
        return false;
      } catch (error) {
        console.error('Error polling status:', error);
        return false;
      }
    };

    const poll = async () => {
      const done = await pollPaymentStatus();
      if (!done && pollCount < 10) {
        setPollCount(prev => prev + 1);
        setTimeout(poll, 2000);
      } else if (!done) {
        setStatus('pending');
      }
    };

    poll();
  }, [sessionId, pollCount]);

  // Auto-redirect countdown when payment is successful
  useEffect(() => {
    if (status === 'success' && paymentData?.site_slug && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && paymentData?.site_slug && countdown === 0) {
      navigate(`/evento/${paymentData.site_slug}`);
    }
  }, [status, paymentData, countdown, navigate]);

  const getEventUrl = () => {
    if (paymentData?.site_slug) {
      return `/evento/${paymentData.site_slug}`;
    }
    return '/';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((value || 0) / 100);
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="font-serif text-2xl text-[#6E0F1A] mb-2">Verificando Pagamento...</h2>
            <p className="text-[#6E0F1A]/70">Aguarde enquanto confirmamos seu pagamento</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#567D50] rounded-full flex items-center justify-center mx-auto mb-6 animate-fade-in">
              <Check className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-serif text-3xl text-[#6E0F1A] mb-4">Pagamento Confirmado!</h2>
            <p className="text-[#6E0F1A]/70 mb-6 max-w-md mx-auto">
              Obrigado pela sua contribuição! Os anfitriões ficarão muito felizes com seu presente.
            </p>
            {paymentData && (
              <div className="bg-[#FAFAFA] rounded-lg p-4 mb-8 inline-block">
                <p className="text-[#6E0F1A]/60 text-sm">Valor pago</p>
                <p className="text-2xl font-semibold text-[#567D50]">
                  {formatCurrency(paymentData.amount_total)}
                </p>
              </div>
            )}
            {paymentData?.site_slug && (
              <p className="text-sm text-[#6E0F1A]/60 mb-4">
                Redirecionando para o evento em {countdown} segundos...
              </p>
            )}
            <div>
              <Link to={getEventUrl()}>
                <Button className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Voltar ao Evento
                </Button>
              </Link>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-serif text-3xl text-[#6E0F1A] mb-4">Pagamento em Processamento</h2>
            <p className="text-[#6E0F1A]/70 mb-6 max-w-md mx-auto">
              Seu pagamento está sendo processado. Você receberá uma confirmação por e-mail assim que for concluído.
            </p>
            <Link to={getEventUrl()}>
              <Button variant="outline" className="border-[#B3121B] text-[#B3121B] rounded-full px-8 py-6">
                <ArrowRight className="w-5 h-5 mr-2" />
                Voltar ao Evento
              </Button>
            </Link>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-[#B3121B]" />
            </div>
            <h2 className="font-serif text-3xl text-[#6E0F1A] mb-4">Sessão Expirada</h2>
            <p className="text-[#6E0F1A]/70 mb-6 max-w-md mx-auto">
              Sua sessão de pagamento expirou. Por favor, tente novamente.
            </p>
            <Button
              onClick={() => window.history.back()}
              className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full px-8 py-6"
            >
              Tentar Novamente
            </Button>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#F3C9CF] rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-[#B3121B]" />
            </div>
            <h2 className="font-serif text-3xl text-[#6E0F1A] mb-4">Algo deu errado</h2>
            <p className="text-[#6E0F1A]/70 mb-6 max-w-md mx-auto">
              Não foi possível verificar o status do pagamento. Entre em contato conosco se o valor foi debitado.
            </p>
            <Link to={getEventUrl()}>
              <Button variant="outline" className="border-[#B3121B] text-[#B3121B] rounded-full px-8 py-6">
                <ArrowRight className="w-5 h-5 mr-2" />
                Voltar ao Evento
              </Button>
            </Link>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] relative">
      <div className="absolute inset-0 noise-overlay"></div>
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#F3C9CF]/30">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-center">
          <Heart className="w-8 h-8 text-[#B3121B] fill-[#B3121B]" />
          <span className="font-cursive text-2xl text-[#6E0F1A] ml-2">Fiesta Lab</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12 relative z-10">
        <Card className="card-dashboard">
          <CardContent className="p-8">
            {renderContent()}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
